import React, { useState } from 'react'
import { IReferenceBillingTariff } from '@app/interfaces/reference-billing'
import {
  useGetAllBillingTariffHistoryQuery,
  useGetOneBillingTariffHistoryQuery,
} from '@app/store/rtk/api/billing-tariffs-api'
import { Pagination, Spin, Table } from 'antd'
import { useTranslation } from 'react-i18next'
import useLocalization from '@app/hooks/useLocalization'
import { skipToken } from '@reduxjs/toolkit/dist/query'

type Props = {
  tariff?: IReferenceBillingTariff | null
}

export const TariffChangeHistory = (props: Props) => {
  const { t } = useTranslation()
  const { localizeData } = useLocalization()

  const all_history_columns = [
    {
      title: t('billing.allHistoryChanges'),
      children: [
        {
          title: '№',
          dataIndex: 'user_id',
          key: 'index',
          width: 50,
          align: 'center',
        },
        {
          title: t('billing.organization_location'),
          key: 'locality_name',
          width: 150,
          dataIndex: 'locality_name',
        },
        {
          title: t('billing.work_time'),
          key: 'work_time',
          width: 150,
          dataIndex: 'work_time',
        },
        {
          title: t('billing.user_name'),
          key: 'user_name',
          width: 150,
          dataIndex: 'user_name',
        },
        {
          title: t('billing.foundation_date'),
          key: 'foundation_date',
          width: 200,
          dataIndex: 'foundation_date',
        },
        {
          title: t('billing.change_date'),
          key: 'change_date',
          width: 200,
          dataIndex: 'change_date',
        },
        {
          title: t('billing.history_type'),
          key: 'type',
          width: 200,
          dataIndex: 'type',
        },
      ],
    },
  ]

  const one_tariff_columns = [
    {
      title: `${
        localizeData(props.tariff?.organizationLocality.data)?.name ?? ''
      } - ${localizeData(props.tariff?.organizationSchedule.data)?.name ?? ''}`,
      children: [
        {
          title: '№',
          dataIndex: 'user_id',
          key: 'index',
          width: 50,
          align: 'center',
        },
        {
          title: t('billing.user_name'),
          key: 'user_name',
          width: 150,
          dataIndex: 'user_name',
        },
        {
          title: t('billing.foundation_date'),
          key: 'foundation_date',
          width: 200,
          dataIndex: 'foundation_date',
        },
        {
          title: t('billing.change_date'),
          key: 'change_date',
          width: 200,
          dataIndex: 'change_date',
        },
        {
          title: t('billing.history_type'),
          key: 'type',
          width: 200,
          dataIndex: 'type',
        },
      ],
    },
  ]

  const { data: oneBillingTariffHistory, isLoading: oneTariffLoading } =
    useGetOneBillingTariffHistoryQuery(props.tariff?.id ?? skipToken)

  const [page, setPage] = useState(1)
  const [size, setSize] = useState(20)
  const {
    data: allBillingTariffHistory,
    isLoading: allTariffLoading,
    isFetching,
  } = useGetAllBillingTariffHistoryQuery(
    props.tariff?.id ? skipToken : { page: page - 1, size },
  )

  const oneDataSource = oneBillingTariffHistory?.response?.map(
    (item, index) => ({
      user_id: index + 1,
      user_name: item.username,
      foundation_date: item.purpose,
      change_date: item.createdDate,
      type: item.revisionType,
    }),
  )

  const allDataSource = allBillingTariffHistory?.response?.map(
    (item, index) => ({
      user_id: (page - 1) * size + index + 1,
      locality_name: localizeData(item.locality.data)?.name,
      work_time: localizeData(item.organizationSchedule.data)?.name,
      user_name: item.username,
      foundation_date: item.purpose,
      change_date: item.createdDate,
      type: item.revisionType,
    }),
  )

  return (
    <div>
      {props.tariff ? (
        <Spin spinning={oneTariffLoading}>
          <Table
            columns={one_tariff_columns as any}
            pagination={false}
            dataSource={oneDataSource}
          />
        </Spin>
      ) : (
        <Spin spinning={allTariffLoading || isFetching}>
          <Table
            columns={all_history_columns as any}
            pagination={false}
            dataSource={allDataSource}
            footer={() => (
              <Pagination
                current={page}
                pageSize={size}
                total={allBillingTariffHistory?.meta?.totalCount ?? 0}
                pageSizeOptions={[20, 50, 100]}
                onChange={(page, size) => {
                  setPage(page)
                  setSize(size)
                }}
              />
            )}
          />
        </Spin>
      )}
      <br />
    </div>
  )
}
